import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { inr } from "@/lib/branch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const CreateBranchDialog = ({ open, onOpenChange, userName }) => {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  useEffect(() => { if (open) api.get("/branches/config").then(({ data }) => setCfg(data)).catch(() => toast.error("Could not load branch settings")); }, [open]);

  const h = cfg?.hamper;
  const gst = h ? Math.round(h.price * (h.gst_percent ?? 5)) / 100 : 0;
  const limitReached = cfg && cfg.branch_count >= cfg.max_branches_per_user;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/branches", { name });
      toast.success(`Branch ${data.branch.referral_code} created — complete payment to activate`);
      navigate(`/checkout/direct/${data.order_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create branch");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="create-branch-dialog">
        <DialogHeader><DialogTitle className="font-display text-[#7F1D1D]">Create New Branch</DialogTitle></DialogHeader>
        {!cfg ? <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#DC2626]" /></div> : (
          <form onSubmit={submit} className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <Info label="Account" value={userName} />
              <Info label="Existing branches" value={`${cfg.branch_count} / ${cfg.max_branches_per_user}`} testid="create-branch-count" />
              <Info label="Original sponsor" value={cfg.original_sponsor ? `${cfg.original_sponsor.name} (${cfg.original_sponsor.user_code})` : "No sponsor"} />
              <Info label="Sponsor branch" value={cfg.original_sponsor_branch_code || "—"} />
            </div>
            <p className="text-xs text-[#64748B] bg-[#FEF2F2] rounded-xl p-3">The new branch keeps your original sponsor. It gets its own branch ID, referral code and link, and goes live once its hamper payment is verified.</p>
            <div>
              <label className="font-medium text-[#7F1D1D]">Branch name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} minLength={cfg.name_min_length} maxLength={cfg.name_max_length} required
                className="input-soft mt-1" placeholder="e.g. Growth Branch" data-testid="create-branch-name-input" />
            </div>
            {h && (
              <div className="flex gap-3 border border-[#E2E8F0] rounded-2xl p-3" data-testid="create-branch-hamper">
                {h.images?.[0] && <img src={h.images[0]} alt={h.name} className="w-20 h-20 rounded-xl object-cover" />}
                <div className="flex-1">
                  <div className="font-semibold text-[#7F1D1D]">{h.name}</div>
                  <p className="text-xs text-[#64748B] line-clamp-2">{h.description}</p>
                  <div className="text-xs mt-1">Price {inr(h.price)} · GST {inr(gst)} · Shipping ₹0</div>
                  <div className="font-numbers font-bold text-[#DC2626]" data-testid="create-branch-total">Payable {inr(h.price + gst)}</div>
                </div>
              </div>
            )}
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1 accent-[#DC2626]" data-testid="create-branch-terms-checkbox" />
              <span>I agree to the <button type="button" onClick={() => setShowTerms(!showTerms)} className="underline text-[#B91C1C]">Terms & Conditions</button>, including the refund and cancellation terms. Earnings are not guaranteed.</span>
            </label>
            {showTerms && <div className="max-h-40 overflow-y-auto text-xs border rounded-xl p-3 prose-sm" dangerouslySetInnerHTML={{ __html: cfg.terms_content }} />}
            {!cfg.enabled && <p className="text-red-600 text-xs" data-testid="create-branch-disabled">Branch creation is currently disabled by admin.</p>}
            {limitReached && <p className="text-red-600 text-xs" data-testid="create-branch-limit">You have reached the branch limit.</p>}
            <button disabled={busy || !agree || !cfg.enabled || limitReached} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="create-branch-submit-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create & proceed to payment"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Info = ({ label, value, testid }) => (
  <div className="bg-[#F8FAFC] rounded-xl p-2.5" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">{label}</div>
    <div className="font-medium text-[#7F1D1D] truncate">{value}</div>
  </div>
);
